import { useEffect, useMemo } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import * as Sentry from '@sentry/react';
import { RootState } from '@/store/store';
import { isEnabled as isSentryEnabled } from '@/lib/sentry/sentry';
import { useToastCtx } from '@/context/ToastContext/ToastContext';
import { useDebounce } from '@/hooks/useDebounce';
import useIsMemberAuthorized from '@/hooks/useIsMemberAuthorized';
import { usePerpsData } from '@/pages/HypertrackerPerps/usePerpsData';
import { buildersDataByAddress } from '@/lib/constants/hyperTrackerBuildersConstants';
import { segmentsLabels } from '@/lib/constants/hyperTrackerConstants';
import { isValidHyperliquidAddress, createUrl } from '@/lib/helpers/hypertrackerHelpers';
import { shortenString } from '@/lib/helpers/helpers';
import { ROUTES } from '@/lib/constants';
import { searchGuest, searchMembers } from '@/api/hypertrackerApi';
import { getCachedGuestToken } from '@/lib/hypertracker/useGuestSearchToken';
import { selectBuildersForSearch } from '@/state/hypertracker/hypertrackerSelector';
import { setBuildersForSearch } from '@/state/hypertracker/hypertackerSlice';
import { sortByRelevance } from './sortByRelevance';
import { SearchResultEntry } from './unifiedSearchTypes';

const MIN_CHARS = 1;
export const MIN_CHARS_FE_LOCAL = 1;

export const useUnifiedSearch = (rawSearchText: string) => {
    const { t } = useTranslation('translation', { keyPrefix: 'hypersearch' });
    const { t: tGlobal } = useTranslation('translation');
    const isMemberAuthorized = useIsMemberAuthorized();
    const { toggleToast } = useToastCtx();
    const dispatch = useDispatch();
    const buildersForSearch = useSelector((state: RootState) => selectBuildersForSearch(state));

    const trimmed = useMemo(() => rawSearchText.trim().replace(/^@/, ''), [rawSearchText]);
    const debouncedTrimmed = useDebounce(trimmed, 400);

    const {
        data: { assetsActiveData },
    } = usePerpsData();

    const tickerList = useMemo(
        () => Object.keys(assetsActiveData?.coins || {}),
        [assetsActiveData],
    );

    const cohortEntries = useMemo(() => Object.entries(segmentsLabels), []);

    const staticBuilderEntries = useMemo(
        () =>
            Object.entries(buildersDataByAddress).map(
                ([address, data]) => [address, data.name] as [string, string],
            ),
        [],
    );

    const isFullAddress = isValidHyperliquidAddress(trimmed);
    const isFeQueryActive = trimmed.length >= MIN_CHARS_FE_LOCAL;

    useEffect(() => {
        if (!isFullAddress || buildersForSearch.length > 0) return;

        const fetchBuilders = async () => {
            try {
                const url = createUrl('/builders_all.json');
                const response = await fetch(url);
                const data = await response.json();
                dispatch(setBuildersForSearch(data?.builders || []));
            } catch (error) {
                console.error('Error fetching builders data:', error);
                if (isSentryEnabled()) Sentry.captureException(error);
            }
        };

        fetchBuilders();
    }, [isFullAddress, buildersForSearch.length, dispatch]);

    const remoteQuery = useQuery({
        queryKey: ['unified-search', debouncedTrimmed, isMemberAuthorized],
        queryFn: async () => {
            try {
                if (isMemberAuthorized) {
                    const { data } = await searchMembers({ q: debouncedTrimmed });
                    return data;
                }

                const token = await getCachedGuestToken();
                try {
                    const { data } = await searchGuest({ q: debouncedTrimmed }, token);
                    return data;
                } catch (error) {
                    if (isAxiosError(error) && error.response?.status === 401) {
                        const refreshedToken = await getCachedGuestToken(true);
                        const { data } = await searchGuest({ q: debouncedTrimmed }, refreshedToken);
                        return data;
                    }
                    throw error;
                }
            } catch (error) {
                console.error('Unified search request failed:', error);
                if (isSentryEnabled()) Sentry.captureException(error);
                toggleToast(true, {
                    label: t('search_error'),
                    severity: 'error',
                    icon: 'error',
                });
                throw error;
            }
        },
        enabled: debouncedTrimmed.length >= MIN_CHARS,
        placeholderData: keepPreviousData,
        staleTime: 30_000,
        retry: false,
    });

    const entries = useMemo<SearchResultEntry[]>(() => {
        if (!isFeQueryActive) return [];

        const result: SearchResultEntry[] = [];
        const seenWalletAddresses = new Set<string>();
        const seenBuilderAddresses = new Set<string>();

        if (isFullAddress) {
            seenWalletAddresses.add(trimmed.toLowerCase());
            result.push({
                group: 'wallet',
                label: t('wallet'),
                displayValue: trimmed,
                matchValue: trimmed,
                url: `${ROUTES.HYPERTRACKER_WALLET}/${trimmed}`,
            });

            const matchedBuilder = buildersForSearch.find(
                (item) => item.address.toLowerCase() === trimmed.toLowerCase(),
            );
            if (matchedBuilder) {
                const builderName =
                    buildersDataByAddress?.[matchedBuilder.address.toLowerCase()]?.name ||
                    matchedBuilder.address;
                seenBuilderAddresses.add(matchedBuilder.address.toLowerCase());
                result.push({
                    group: 'builder',
                    label: t('builder'),
                    displayValue: builderName,
                    matchValue: trimmed,
                    url: `${ROUTES.HYPERTRACKER_BUILDER_PERSONAL}/${matchedBuilder.address}`,
                });
            }
        }

        (remoteQuery.data?.wallets || []).forEach(({ address }) => {
            if (seenWalletAddresses.has(address.toLowerCase())) return;
            seenWalletAddresses.add(address.toLowerCase());
            result.push({
                group: 'wallet',
                label: t('wallet'),
                displayValue: address,
                matchValue: address,
                url: `${ROUTES.HYPERTRACKER_WALLET}/${address}`,
            });
        });

        const normalizedHlQuery = trimmed.toLowerCase().replace(/\.hl$/, '');
        const isExactHlMatch = (hlName: string) =>
            hlName.toLowerCase().replace(/\.hl$/, '') === normalizedHlQuery;
        (remoteQuery.data?.hlNames || [])
            .toSorted((a, b) => Number(isExactHlMatch(b.hlName)) - Number(isExactHlMatch(a.hlName)))
            .forEach(({ hlName, address }) => {
                result.push({
                    group: 'hlName',
                    label: t('hl_name'),
                    displayValue: `${hlName} (${shortenString(address)})`,
                    matchValue: hlName,
                    url: `${ROUTES.HYPERTRACKER_WALLET}/${address}`,
                });
            });

        const isExactHyperliquidNameMatch = (displayName: string) =>
            displayName.toLowerCase() === trimmed.toLowerCase();
        (remoteQuery.data?.hyperliquidNames || [])
            .toSorted(
                (a, b) =>
                    Number(isExactHyperliquidNameMatch(b.displayName)) -
                    Number(isExactHyperliquidNameMatch(a.displayName)),
            )
            .forEach(({ displayName, address }) => {
                result.push({
                    group: 'hyperliquidName',
                    label: t('hyperliquid_name'),
                    displayValue: `${displayName} (${shortenString(address)})`,
                    matchValue: displayName,
                    url: `${ROUTES.HYPERTRACKER_WALLET}/${address}`,
                });
            });

        (remoteQuery.data?.refCodes || []).forEach(({ refCode, address }) => {
            result.push({
                group: 'refCode',
                label: t('ref_code'),
                displayValue: `${refCode} (${shortenString(address)})`,
                matchValue: refCode,
                url: `${ROUTES.HYPERTRACKER_WALLET}/${address}`,
            });
        });

        (remoteQuery.data?.usernames || []).forEach(({ username, address }) => {
            result.push({
                group: 'username',
                label: t('username'),
                displayValue: `${username} (${shortenString(address)})`,
                matchValue: username,
                url: `${ROUTES.HYPERTRACKER_WALLET}/${address}`,
            });
        });

        (remoteQuery.data?.twitter || []).forEach(({ screenName, address }) => {
            result.push({
                group: 'twitter',
                label: t('twitter'),
                displayValue: `@${screenName} (${shortenString(address)})`,
                matchValue: screenName,
                url: `${ROUTES.HYPERTRACKER_WALLET}/${address}`,
            });
        });

        sortByRelevance(tickerList, trimmed, (ticker) => ticker).forEach((ticker) => {
            result.push({
                group: 'ticker',
                label: t('ticker'),
                displayValue: ticker,
                matchValue: ticker,
                url: `${ROUTES.HYPERTRACKER_PERPS}/${ticker.toLowerCase()}`,
            });
        });

        const translatedCohorts = cohortEntries.map(
            ([segmentId, labelKey]) => [segmentId, tGlobal(labelKey)] as [string, string],
        );
        sortByRelevance(translatedCohorts, trimmed, (item) => item[1]).forEach(
            ([segmentId, label]) => {
                result.push({
                    group: 'cohort',
                    label: t('cohort'),
                    displayValue: label,
                    matchValue: label,
                    url: `${ROUTES.HYPERTRACKER_SEGMENTS}/${segmentId}`,
                });
            },
        );

        sortByRelevance(staticBuilderEntries, trimmed, (item) => item[1]).forEach(
            ([address, name]) => {
                if (seenBuilderAddresses.has(address.toLowerCase())) return;
                seenBuilderAddresses.add(address.toLowerCase());
                result.push({
                    group: 'builder',
                    label: t('builder'),
                    displayValue: name,
                    matchValue: name,
                    url: `${ROUTES.HYPERTRACKER_BUILDER_PERSONAL}/${address}`,
                });
            },
        );

        (remoteQuery.data?.builders || []).forEach(({ name, address }) => {
            if (seenBuilderAddresses.has(address.toLowerCase())) return;
            seenBuilderAddresses.add(address.toLowerCase());
            result.push({
                group: 'builder',
                label: t('builder'),
                displayValue: name,
                matchValue: name,
                url: `${ROUTES.HYPERTRACKER_BUILDER_PERSONAL}/${address}`,
            });
        });

        return result;
    }, [
        isFeQueryActive,
        isFullAddress,
        trimmed,
        tickerList,
        cohortEntries,
        staticBuilderEntries,
        buildersForSearch,
        remoteQuery.data,
        t,
        tGlobal,
    ]);

    return {
        entries,
        isLoadingRemote: remoteQuery.isFetching,
        isFeQueryActive,
    };
};
